'use client';

import { useState } from 'react';
import { useAuth } from '@/platform/auth/useAuth';
import {
  addCartItem,
  cartLineForProduct,
  type SellerCart,
} from '@/platform/commerce/api';
import type { VendorProduct } from '@/platform/vendor/api';

type Props = {
  vendorUserId: string;
  product: VendorProduct;
  openCart: SellerCart | null;
  cartLoading?: boolean;
  onCartChange: (cart: SellerCart | null) => void;
  onOpenCart: () => void;
};

function maxQuantity(product: VendorProduct) {
  if (product.trackInventory && product.inventoryQty != null) {
    return Math.max(0, Math.min(99, product.inventoryQty));
  }
  return 99;
}

export function ProductPurchaseControls({
  vendorUserId,
  product,
  openCart,
  cartLoading = false,
  onCartChange,
  onOpenCart,
}: Props) {
  const { user } = useAuth();
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const maxQty = maxQuantity(product);
  const outOfStock = product.trackInventory && maxQty < 1;
  const isOwnListing = !!user?.uid && user.uid === vendorUserId;
  const inCartLine = cartLineForProduct(openCart, product.id);

  async function handleAddToCart() {
    if (outOfStock || isOwnListing) return;
    setLoading(true);
    setError('');
    try {
      const result = await addCartItem(vendorUserId, { productId: product.id, quantity });
      onCartChange(result.cart);
      setQuantity(1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add to cart');
    } finally {
      setLoading(false);
    }
  }

  if (outOfStock) {
    return (
      <div className="vendor-buy-now product-purchase-controls">
        <button type="button" className="btn btn-primary" disabled>
          Out of stock
        </button>
      </div>
    );
  }

  if (isOwnListing) {
    return <p className="text-muted">You can&apos;t buy your own listing</p>;
  }

  return (
    <div className="vendor-buy-now product-purchase-controls">
      <label className="marketplace-purchase__qty">
        Quantity
        <input
          type="number"
          min={1}
          max={maxQty}
          value={quantity}
          disabled={loading || cartLoading}
          onChange={(e) => setQuantity(Math.max(1, Math.min(maxQty, Number(e.target.value) || 1)))}
        />
      </label>
      {inCartLine && (
        <p className="text-muted product-purchase-controls__in-cart">
          In cart: {inCartLine.quantity}{' '}
          <button type="button" className="btn btn-link" onClick={onOpenCart}>
            View cart
          </button>
        </p>
      )}
      {error && <p className="form-error">{error}</p>}
      <button
        type="button"
        className="btn btn-primary"
        disabled={loading || cartLoading}
        onClick={() => void handleAddToCart()}
      >
        {loading ? 'Adding…' : inCartLine ? 'Update cart' : 'Add to cart'}
      </button>
    </div>
  );
}
